import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Jobs-style filter pill select for list toolbars:
 * icon + Label [: Value] + chevron + circular clear.
 */
@Component({
  selector: 'app-toolbar-filter-select',
  template: `
    <div
      class="pp-tfs-host"
      [class.pp-tfs-host--on]="hasValue"
      [class.pp-tfs-host--disabled]="disabled">
      <i *ngIf="icon" class="pi pp-tfs-host__icon" [ngClass]="icon" aria-hidden="true"></i>
      <p-dropdown
        [options]="options || []"
        [optionLabel]="optionLabel"
        [optionValue]="optionValue"
        [placeholder]="filterLabel"
        [showClear]="false"
        [filter]="filter"
        [filterBy]="filterBy || optionLabel"
        [appendTo]="appendTo"
        [disabled]="disabled"
        [styleClass]="'pp-tfs-dd'"
        [(ngModel)]="inner"
        (ngModelChange)="onInnerChange($event)"
        (onChange)="changed.emit($event)">
        <ng-template let-item pTemplate="selectedItem">
          <span class="pp-tfs">
            <span class="pp-tfs__label">{{ filterLabel }}</span>
            <strong
              *ngIf="labelOf(item) as text"
              class="pp-tfs__value"
              [attr.title]="text">{{ text }}</strong>
          </span>
        </ng-template>
      </p-dropdown>
      <button
        *ngIf="hasValue && showClear && !disabled"
        type="button"
        class="pp-tfs-host__x"
        (click)="clear($event)"
        [attr.aria-label]="'Clear ' + filterLabel">
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToolbarFilterSelectComponent),
      multi: true,
    },
  ],
})
export class ToolbarFilterSelectComponent implements ControlValueAccessor {
  @Input() filterLabel = 'Filter';
  /** PrimeIcons class, e.g. `pi-briefcase`. */
  @Input() icon = '';
  @Input() options: any[] = [];
  @Input() optionLabel = 'label';
  /** When empty, the whole option object is bound. */
  @Input() optionValue = '';
  @Input() showClear = true;
  @Input() filter = false;
  @Input() filterBy = '';
  @Input() appendTo: any = 'body';

  @Output() changed = new EventEmitter<any>();
  @Output() cleared = new EventEmitter<void>();

  inner: any = null;
  disabled = false;

  private onChange: (v: any) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get hasValue(): boolean {
    return this.inner !== null && this.inner !== undefined && this.inner !== '';
  }

  writeValue(value: any): void {
    this.inner = value ?? null;
  }

  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInnerChange(value: any): void {
    this.inner = value;
    this.onChange(value);
    this.onTouched();
  }

  clear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.inner = null;
    this.onChange(null);
    this.onTouched();
    this.cleared.emit();
    this.changed.emit({ value: null, originalEvent: event });
  }

  labelOf(item: any): string {
    if (item == null) return '';
    if (typeof item === 'object') {
      const text = item[this.optionLabel];
      return text != null && text !== '' ? String(text) : '';
    }
    if (!this.optionValue) return String(item);
    const found = (this.options || []).find(
      (o) => String(o?.[this.optionValue]) === String(item)
    );
    if (found) {
      const text = found[this.optionLabel];
      return text != null && text !== '' ? String(text) : String(item);
    }
    return String(item);
  }
}
